/**
 * Image optimization utilities for Supabase Storage
 * Uses Supabase Image Transformation API with render endpoint
 * @see https://supabase.com/docs/guides/storage/serving/image-transformations
 */

const SUPABASE_PROJECT_ID = 'khrlghyyuqyfxeirybxc';
const SUPABASE_STORAGE_BASE = `https://${SUPABASE_PROJECT_ID}.supabase.co/storage/v1`;

/**
 * Check if the URL is from Supabase Storage
 */
export const isSupabaseStorageUrl = (url: string): boolean => {
  return url.includes('supabase.co/storage') || url.includes(SUPABASE_PROJECT_ID);
};

/**
 * Get optimized image URL using Supabase Image Transformation API
 * Converts /object/public/bucket/path to /render/image/public/bucket/path?params
 * 
 * @param originalUrl - Original image URL from Supabase Storage
 * @param width - Target width in pixels
 * @param quality - Image quality 1-100 (default 60 for better compression)
 * @param height - Optional height for cropping
 */
export const getOptimizedImageUrl = (
  originalUrl: string,
  width: number = 400,
  quality: number = 75
): string => {
  if (!originalUrl || originalUrl === '/placeholder.svg') {
    return originalUrl;
  }

  // Only transform Supabase Storage URLs
  if (!isSupabaseStorageUrl(originalUrl)) {
    return originalUrl;
  }

  // Check if URL already has transformation params or render path
  if (originalUrl.includes('?') || originalUrl.includes('/render/image/')) {
    return originalUrl;
  }

  try {
    // Use wsrv.nl for reliable image optimization (resizing + WebP conversion)
    // This solves the issue where Supabase Image Transformation is not enabled/configured
    // and ensures images are always served in optimal size and format (WebP).
    // Docs: https://wsrv.nl/
    
    // 1. Clean the URL (remove existing query params if they conflict, though wsrv handles them)
    // We pass the full original URL to wsrv
    
    // 2. Construct wsrv.nl URL
    const wsrvParams = new URLSearchParams({
      url: originalUrl,
      w: width.toString(),
      q: quality.toString(),
      output: 'webp',
      we: 'true', // Enhance (sharpen) slightly for better perception
      il: 'true', // Progressive (interlace)
      n: '-1' 
    });

    return `https://wsrv.nl/?${wsrvParams.toString()}`;
  } catch {
    return originalUrl;
  }
};

/**
 * Get srcset for responsive images - reduced sizes for faster loading
 * @param originalUrl - Original image URL
 * @param sizes - Array of widths to generate (smaller defaults for cards)
 */
export const getResponsiveSrcSet = (
  originalUrl: string,
  sizes: number[] = [300, 400, 500],
  quality: number = 75
): string => {
  if (!originalUrl || !isSupabaseStorageUrl(originalUrl)) {
    return '';
  }

  return sizes
    .map(size => `${getOptimizedImageUrl(originalUrl, size, quality)} ${size}w`)
    .join(', ');
};

/**
 * Image size presets optimized for actual container sizes
 * Reduced from original to match actual display dimensions
 */
export const IMAGE_SIZES = {
  thumbnail: { width: 150, quality: 70 },
  card: { width: 380, quality: 75 },         // Reduced from 400 to 380 to match exact container width (378px)
  cardMobile: { width: 350, quality: 75 },
  cardDesktop: { width: 450, quality: 80 },
  hero: { width: 1000, quality: 80 },
  heroMobile: { width: 500, quality: 75 },
  full: { width: 1200, quality: 80 },
  dialog: { width: 800, quality: 65 }
} as const;

/**
 * Get image URL with preset
 */
export const getImageWithPreset = (
  originalUrl: string,
  preset: keyof typeof IMAGE_SIZES
): string => {
  const config = IMAGE_SIZES[preset];
  return getOptimizedImageUrl(originalUrl, config.width, config.quality);
};
